import classNames from 'classnames';
import * as React from 'react';

import Container from '@Components/layout/Container';
import Row from '@Components/layout/Row';
import { OptionalProps } from '@Utils/types';

import styles from './Tabs.module.scss';

type Justify = 'center' | 'start';

type BackgroundColor = 'grayGreen' | 'white' | 'transparent';

const tabPanelColor: Record<BackgroundColor, `tabPanelColor${Capitalize<BackgroundColor>}`> = {
  grayGreen: 'tabPanelColorGrayGreen',
  white: 'tabPanelColorWhite',
  transparent: 'tabPanelColorTransparent',
} as const;

interface TabsProps {
  tabs: (string | JSX.Element | (() => JSX.Element))[];
  tabContents: JSX.Element | JSX.Element[] | (() => JSX.Element)[];
  initialTab: number;
  justify?: Justify;
  noShadow?: boolean;
  backgroundColor?: BackgroundColor;
  className?: OptionalProps<typeof styles>;
  onTabChange?: (activeTab: number) => void;
}

const Tabs: React.FunctionComponent<TabsProps> = ({
  tabs,
  tabContents,
  noShadow,
  initialTab,
  justify = 'center',
  backgroundColor = 'white',
  className,
  onTabChange,
}) => {
  const [activeTab, toggleActiveTab] = React.useState(initialTab);

  React.useEffect(() => {
    toggleActiveTab(initialTab && initialTab > tabs.length - 1 ? 0 : initialTab);
  }, [initialTab]);

  React.useEffect(() => {
    if (onTabChange) {
      onTabChange(activeTab);
    }
  }, [activeTab]);

  const renderTabs = (item: (() => React.ReactElement) | string | JSX.Element, index: number) => {
    return (
      <button
        key={index}
        style={justify === 'center' ? { width: `${100 / tabs.length}%` } : {}}
        onClick={() => toggleActiveTab(index)}
        className={classNames(
          styles.tab,
          justify === 'start' && styles.tabJustifyStart,
          index === activeTab && styles.active,
        )}
      >
        <span>{typeof item === 'function' ? item() : item}</span>
      </button>
    );
  };

  const renderContents = (item: (() => JSX.Element) | JSX.Element, index: number) => {
    if (index === activeTab) {
      return <div key={index}>{typeof item === 'function' ? item() : item}</div>;
    }
    return null;
  };

  const tabPanelClassNames = classNames(styles.tabPanel);
  const tabsContainer = classNames(styles.tabsContainer, justify === 'start' && styles.tabsContainerJustifyStart);

  const tabsWrapper = classNames(
    styles.tabsWrapper,
    styles[tabPanelColor[backgroundColor]],
    noShadow && styles.noShadow,
  );

  return (
    <div>
      <Container>
        <Row justifyBetween>
          <div className={classNames(tabPanelClassNames, className?.tabPanel)}>
            <div className={classNames(tabsWrapper, className?.tabsWrapper)}>
              <div className={tabsContainer}>{tabs.map((item, i) => renderTabs(item, i))}</div>
            </div>
          </div>
        </Row>
      </Container>
      <div className={styles.tabContentContainer}>
        {Array.isArray(tabContents) ? tabContents.map((item, i) => renderContents(item, i)) : <div>{tabContents}</div>}
      </div>
    </div>
  );
};

export default Tabs;
